const en = {
  home: {
    hero: {
      alt: 'Panch Tatva logo',
      tagline: 'A quiet exploration of balance, presence, and the five elements.',
      cta: 'Enter the Elements',
    },
    collection: {
      title: 'The Elements',
      description:
        'Each element reflects a state of being — expressed through form, texture, and intention.',
    },
    about: {
      title: 'Our Philosophy',
      paragraphs: [
        'Panch Tatva is a philosophy rooted in the understanding that balance is not created — it is remembered.',
        'Through elemental awareness and mindful creation, the work explores how everyday objects can support presence, rhythm, and quiet alignment in modern life.',
      ],
    },
  },
  nav: {
    home: 'Home',
    shop: 'The Elements',
    about: 'Our Philosophy',
    cart: 'Cart',
    logoAlt: 'Panch Tatva by Anukta logo',
  },
  footer: {
    brand: 'Panch Tatva',
    tagline: 'A philosophy of elemental balance expressed through mindful creations.',
    explore: ['Home', 'The Elements', 'Philosophy', 'Rituals', 'Shop'],
    exploreTitle: 'Explore',
    contact: {
      email: 'support@panch-tatva.co.in',
      phone: '+91 6390725752',
    },
    contactTitle: 'Contact',
    legal: [
      '“Panch Tatva by Anukta” is a brand and philosophy operated by Anukta Panchatatva LLP.',
      '© 2026 Panch Tatva by Anukta™. All rights reserved.',
    ],
  },
  card: {
    addToCart: 'Add to Cart',
    viewDetails: 'View Details',
  },
  cart: {
    title: 'Your Cart',
    empty: 'Your cart is empty',
    remove: 'Remove',
    totalPrefix: 'Total:',
    checkout: 'Checkout',
    guestBanner: 'Sign in to save your cart across devices',
  },
  checkout: {
    guestSignInPrompt: 'Sign in for faster checkout',
    // Address section
    shippingAddress: 'Shipping Address',
    savedAddresses: 'Saved Addresses',
    addNewAddress: '+ Add New Address',
    useThisAddress: 'Use This Address',
    noSavedAddresses: 'No saved addresses found',
    loadingAddresses: 'Loading your addresses…',
    addressLoadError: 'Could not load saved addresses. Please enter manually.',
    // Address form fields
    fullName: 'Full Name',
    phone: 'Phone Number',
    email: 'Email',
    addressLine: 'Address',
    city: 'City',
    state: 'State',
    pincode: 'Pincode',
    saveAsDefault: 'Save as default address',
    // Shipping options section
    deliveryMethod: 'Delivery Method',
    fetchingRates: 'Fetching shipping rates…',
    shippingRatesFor: 'Rates for',
    noShippingOptions: 'No courier services available for this pincode. Please check the pincode and try again.',
    shippingFallback: 'Live rates unavailable — standard options shown.',
    shippingApiError: 'Could not load shipping rates. Using standard options.',
    ratesUpdating: 'Updating rates…',
    pincodeMissing: 'Enter a valid pincode to load shipping rates.',
    deliveryDays: 'business days',
    // Shipping method labels (static fallback)
    standardDelivery: 'Standard Delivery',
    standardDays: '3–5 business days',
    expressDelivery: 'Express Delivery',
    expressDays: '1–2 business days',
    // Review step
    reviewOrder: 'Review Order',
    deliveryTo: 'Delivery to:',
    // Buttons / navigation
    continue: 'Continue',
    back: 'Back',
    payNow: 'Pay',
    // Step labels
    stepAddress: 'Address',
    stepShipping: 'Shipping',
    stepReview: 'Review',
    // Empty cart
    cartEmpty: 'Your cart is empty',
    cartEmptyDesc: 'Add items before proceeding to checkout',
    continueShopping: 'Continue Shopping',
    // v2.7 — Feature 33
    noCoverage: 'Delivery is not available to this pincode.',
    noCoverageDesc: 'Please check the pincode or contact us.',
    editAddress: 'Edit address',
    serviceUnavailable: 'Delivery options are unavailable right now. Standard options shown.',
    retryRates: 'Retry',
    recommended: 'Recommended',
  },
  auth: {
    loading: 'Loading...',
    greeting: 'Hi',
    login: 'Login',
    logout: 'Logout',
    anonymous: 'User',
  },
  payment: {
    payPrefix: 'Pay',
    verifySuccess: 'Payment verified and successful!',
    verifyFail: 'Payment verification failed. Please contact support.',
    verifyError: 'Payment verification error.',
    initiationFail: 'Payment initiation failed.',
  },
  product: {
    notFound: 'Product not found',
  },
  authPages: {
    loginHeader: 'Login to continue',
    loginPrompt: 'Please login to proceed to payment.',
  },
  profile: {
    header: 'Profile',
    loadingProfile: 'Loading profile...',
    loadingUserInfo: 'Loading user info...',
    labels: {
      name: 'Name',
      email: 'Email',
      firstName: 'First Name',
      lastName: 'Last Name',
    },
  },
  shop: {
    title: 'Shop All Products',
  },
  quiz: {
    cta: 'Find Your Element',
    ctaSubtitle: 'Take our 5-question ritual to discover which element speaks to your skin.',
    title: 'Find Your Element',
    subtitle: 'Answer 5 questions and we\'ll match you with your elemental skin ritual.',
    step: 'Step',
    of: 'of',
    next: 'Next',
    back: 'Back',
    submit: 'Reveal My Element',
    loading: 'Reading the elements…',
    error: 'Something went wrong. Please try again.',
    questions: {
      skinType: {
        label: 'What is your skin type?',
        options: {
          dry: 'Dry — feels tight, needs nourishment',
          oily: 'Oily — shine-prone, needs balance',
          combination: 'Combination — mixed across zones',
          sensitive: 'Sensitive — reacts easily, needs calm',
          normal: 'Normal — generally balanced',
        },
      },
      climate: {
        label: 'What is your climate like?',
        options: {
          hot_humid: 'Hot & Humid',
          dry_arid: 'Dry & Arid',
          cold: 'Cold & Crisp',
          temperate: 'Temperate & Mild',
        },
      },
      concern: {
        label: 'What is your primary skin concern?',
        options: {
          hydration: 'Hydration & Moisture',
          acne: 'Acne & Oiliness',
          anti_aging: 'Anti-Aging & Firmness',
          glow: 'Glow & Brightening',
          soothing: 'Soothing & Calming',
        },
      },
      texture: {
        label: 'What texture do you prefer?',
        options: {
          rich: 'Rich & Creamy',
          light: 'Light & Watery',
          solid: 'Solid Bar — less is more',
          none: 'No preference',
        },
      },
      fragrance: {
        label: 'What scent draws you in?',
        options: {
          earthy: 'Earthy & Woody — grounded',
          fresh: 'Fresh & Floral — uplifting',
          citrus: 'Citrus — energising',
          unscented: 'Unscented — pure',
        },
      },
    },
    result: {
      title: 'Your Element is',
      secondary: 'with a touch of',
      rationale: 'Why this works for you',
      recommendedProducts: 'Your Elemental Picks',
      addSet: 'Add Recommended Set to Cart',
      retake: 'Retake Quiz',
      shopAll: 'Shop All Elements',
      elements: {
        earth: 'Earth',
        water: 'Water',
        fire: 'Fire',
        air: 'Air',
        ether: 'Ether',
      },
      elementDescriptions: {
        earth: 'Grounding · Nourishing · Deep',
        water: 'Hydrating · Cooling · Gentle',
        fire: 'Purifying · Energising · Clear',
        air: 'Refreshing · Light · Free',
        ether: 'Balancing · Subtle · Pure',
      },
    },
  },
  chat: {
    title: 'Tatva',
    subtitle: 'Your elemental guide',
    placeholder: 'Ask me anything…',
    send: 'Send',
    loading: 'Thinking…',
    error: 'Could not reach Tatva. Please try again.',
    escalatePrompt: 'Would you like me to raise a support ticket?',
    escalateYes: 'Yes, please',
    escalateNo: 'No, thanks',
    ticketSuccess: 'Support ticket raised. We\'ll get back to you within 24 hours.',
    ticketError: 'Could not raise a ticket. Email us at support@panch-tatva.co.in',
    emailPlaceholder: 'Your email address',
    openLabel: 'Chat with Tatva',
    closeLabel: 'Close chat',
  },
  products: {
    '1': {
      name: 'Earth',
      description: 'Grounding & nourishing products crafted with natural clays and herbs, inspired by the wisdom of Mother Earth.',
    },
    '2': {
      name: 'Water',
      description: 'Soothing products infused with aquatic scents and essential oils to calm emotions.',
    },
    '3': {
      name: 'Fire',
      description: 'Invigorating products to energize your space with fiery aromas.',
    },
    '4': {
      name: 'Air',
      description: 'Creations inspired by Air, releasing delicate fragrances as inspiration moves freely and pathways open.',
    },
    '5': {
      name: 'Ether',
      description: 'Crafted for a gentle cleanse - inviting clarity, boundless space, and an unspoken presence into quiet luxury.',
    },
    '6': {
      name: 'Panch Tatva Gift Box',
      description: 'Curated luxury gift box containing all five elemental products for complete balance.',
    },
  },
  admin: {
    // Dashboard
    dashboard: 'Admin Dashboard',
    dashboardSubtitle: 'Manage your Panch Tatva operations',
    totalRevenue: 'Total Revenue',
    totalOrders: 'Total Orders',
    totalCustomers: 'Total Customers',
    pendingActions: 'Pending Actions',
    requiresAttention: 'Requires attention',
    thisMonth: 'This month',
    allTime: 'All time',
    isActive: 'Active',
    topProducts: 'Top Products',
    recentOrders: 'Recent Orders',
    systemHealth: 'System Health',
    razorpay: 'Razorpay',
    database: 'Database',

    // Navigation
    products: 'Products',
    orders: 'Orders',
    customers: 'Customers',
    finance: 'Finance',
    reconciliation: 'Reconciliation',
    settings: 'Settings',
    manage: 'Manage',

    // Products Page
    manageProducts: 'Add, edit, delete products and manage inventory',
    manageYourInventory: 'Manage your product inventory',
    productName: 'Product Name',
    element: 'Element',
    price: 'Price',
    costPrice: 'Cost Price',
    quantity: 'Quantity',
    warehouseLocation: 'Warehouse Location',
    description: 'Description',
    imageUrl: 'Image URL',
    allStatus: 'All Status',
    active: 'Active',
    discontinued: 'Discontinued',
    draft: 'Draft',
    profit: 'Profit Margin',
    searchProducts: 'Search products...',
    noProducts: 'No products found',
    addProduct: '+ Add Product',
    editProduct: 'Edit Product',
    addNewProduct: 'Add New Product',
    actions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    cancel: 'Cancel',
    previous: 'Previous',
    next: 'Next',
    page: 'Page',
    of: 'of',

    // Orders Page
    manageAllOrders: 'View and manage all customer orders',
    manageOrders: 'Track orders, process refunds, create manual orders',
    orderId: 'Order ID',
    customer: 'Customer',
    amount: 'Amount',
    status: 'Status',
    date: 'Date',
    searchOrders: 'Search by order ID, email...',
    noOrders: 'No orders found',
    pendingPayment: 'Pending Payment',
    confirmed: 'Confirmed',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    refunded: 'Refunded',
    refund: 'Refund',
    refundOrder: 'Refund Order',
    refundAmount: 'Refund Amount',
    maxRefund: 'Max',
    reason: 'Reason',
    selectReason: 'Select reason',
    notes: 'Notes',
    processRefund: 'Process Refund',

    // Customers Page
    manageAllCustomers: 'View and manage all customer accounts',
    manageCustomers: 'View customer details and order history',
    email: 'Email',
    phone: 'Phone',
    lastOrder: 'Last Order',
    view: 'View',
    searchCustomers: 'Search by email, name, phone...',
    noCustomers: 'No customers found',
    customerInfo: 'Customer Information',
    createdAt: 'Member Since',
    lastLogin: 'Last Login',
    totalSpent: 'Total Spent',
    avgOrderValue: 'Avg Order',
    memberStatus: 'Status',
    addresses: 'Addresses',
    orderHistory: 'Order History',
    close: 'Close',

    // Finance Page
    viewFinancialData: 'Track transactions and financial reports',
    manageFinance: 'View transactions, P&L reports, accounting',
    netRevenue: 'Net Revenue',
    afterRefunds: 'After refunds',
    grossProfit: 'Gross Profit',
    margin: 'margin',
    netProfit: 'Net Profit',
    roi: 'ROI',
    topProductsByRevenue: 'Top Products by Revenue',
    unitsSold: 'units sold',
    transactionFilters: 'Transaction Filters',
    dateFrom: 'From Date',
    dateTo: 'To Date',
    successful: 'Successful',
    pending: 'Pending',
    failed: 'Failed',
    type: 'Type',
    searchTransactions: 'Search transactions...',
    noTransactions: 'No transactions found',

    // Reconciliation Page
    detectAndResolveIssues: 'Detect and resolve mismatches in inventory, payments, and orders',
    manageReconciliation: 'Detect and resolve mismatches, run audits',
    openIssues: 'Open Issues',
    critical: 'Critical',
    warnings: 'Warnings',
    info: 'Info',
    allSeverity: 'All Severity',
    noIssuesFound: 'No issues found',
    resolveMismatch: 'Resolve Mismatch',
    issue: 'Issue',
    expected: 'Expected',
    actual: 'Actual',
    difference: 'Difference',
    detected: 'Detected',
    product: 'Product',
    resolve: 'Resolve',
    selectAction: 'Select action',
    confirm: 'Confirm',
    resolved: 'Resolved',
    ignored: 'Ignored',

    // Admin Email Whitelist
    adminEmails: 'Admin Email Whitelist',
    emailWhitelistDesc: 'Manage which emails can auto-onboard as admins when they sign up',
    manageEmails: 'Manage Admin Emails',
    addNewAdmin: '+ Add New Admin Email',
    addNewEmail: 'Add New Admin Email',
    addEmail: 'Add Email',
    fullName: 'Full Name',
    invalidEmail: 'Invalid email address',
    emailAdded: 'Email added to whitelist successfully',
    emailRemoved: 'Email removed from whitelist successfully',
    confirmRemove: 'Are you sure you want to remove this email? Users with this email will no longer auto-onboard as admin.',
    noEmails: 'No admin emails whitelisted yet',
    searchEmails: 'Search by email or name...',
    superadminOnly: 'Only superadmins can manage admin emails',
    emailHint: 'Use company domain: @panch-tatva.co.in for easier management',
    reasonExample: 'e.g., Operations Manager',
    reasonHint: 'Internal note: helps track why this person was added',
    adminRole: 'Admin',
    superadminRole: 'Superadmin',
    addFirst: 'Add First Email',
    activatedOn: 'Activated on',
    howItWorks: 'How it works',
    emailAllStatus: 'All Status',
    emailPending: 'Pending (Not Signed Up Yet)',
    emailActive: 'Active (User Signed Up)',
    emailInactive: 'Inactive (Removed)',

    // Admin User Management
    manageAdmins: 'Manage Admins',
    adminUsers: 'Admin Users',
    promoteUser: 'Promote to Admin',
    demoteUser: 'Demote to User',
    viewDetails: 'View Details',
    searchByEmail: 'Search by email or name...',
    filterByRole: 'Filter by role',
    allRoles: 'All Roles',
    roleAdmin: 'Admin',
    roleSuperadmin: 'Superadmin',
    dateAssigned: 'Date Assigned',
    assignedBy: 'Assigned By',
    confirmDemote: 'Confirm Demotion',
    demoteWarning: 'This user will lose all admin access and permissions. This action cannot be undone.',
    noAdminsFound: 'No admin users found',
    selectTargetRole: 'Select target role',
    promoteReason: 'Reason for promotion (optional)',
    demoteReason: 'Reason for demotion (optional)',
    statusHistory: 'Status History',
    promotedOn: 'Promoted on',
    demotedOn: 'Demoted on',
    userNotFound: 'User not found',
    alreadyAdmin: 'This user is already an admin',
    promoteSuccess: 'User promoted successfully',
    demoteSuccess: 'User demoted successfully',
    backToDashboard: '← Back to Dashboard',

    // Common
    loading: 'Loading...',
    error: 'Error loading',
    units: 'units',
  },
  shipment: {
    // Payment phase
    paymentReceived: 'Payment Received!',
    paymentFailed: 'Payment Failed',
    paymentFailedDesc: 'Your payment could not be processed. Please try again.',
    retryPayment: 'Retry Payment',

    // Async processing phase
    creatingShipment: 'Creating Shipment...',
    creatingShipmentDesc: 'We\'re preparing your order for shipment (usually 5–30 seconds).',
    checkingStatus: 'Checking Status...',
    checkingStatusDesc: 'Verifying with our fulfillment partner. This should complete within 5 minutes.',
    delayedDesc: 'We\'re preparing your shipment. Thank you for your patience.',

    // Failure states
    shipmentFailed: 'Shipment Creation Failed',
    shipmentFailedDesc: 'Your shipment couldn\'t be created right now. Our team will review this.',
    permanentFailed: 'Order Needs Attention',
    permanentFailedDesc: 'Your shipment couldn\'t be created after multiple attempts. Please contact support.',
    contactSupport: 'Contact Support',

    // Success / tracking
    shipmentCreated: 'Your Order Has Shipped!',
    shipmentCreatedDesc: 'Your package is on its way. Track your order below.',
    awbCode: 'AWB Code',
    courier: 'Courier',
    trackingStatus: 'Status',
    viewTracking: 'View Real-time Tracking →',
    estimatedDelivery: 'Estimated Delivery',
    inTransit: 'In Transit',
    inTransitDesc: 'Your package is on the way to you.',
    outForDelivery: 'Out for Delivery',
    outForDeliveryDesc: 'Your package will arrive today!',
    delivered: 'Delivered!',
    deliveredDesc: 'Your order has been delivered. Thank you for shopping with us!',
    trackOrder: 'Track Order',
    shopAgain: 'Shop Again',

    // Misc
    orderConfirmedNote: 'Payment verified and your order is confirmed. Shipment is being created in the background.',
    pollingNote: 'This page updates automatically — no need to refresh.',
    retryShipment: 'Retry Shipment',
  },
  orderConfirmation: {
    title: 'Order Confirmed!',
    subtitle: 'Thank you for your purchase. Your order has been confirmed.',
    orderId: 'Order ID',
    orderDate: 'Order Date',
    total: 'Total Amount',
    items: 'Items',
    shippingAddress: 'Shipping Address',
    priceSummary: 'Price Summary',
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    free: 'Free',
    tax: 'Tax (18% GST)',
    confirmationEmail: 'Confirmation Email',
    confirmationEmailDesc: 'A confirmation email has been sent to',
    whatsNext: 'What\'s Next?',
    orderConfirmedStep: 'Order Confirmed',
    preparingStep: 'Preparing',
    shippedStep: 'Shipped',
    deliveredStep: 'Delivered',
    viewOrderHistory: 'View Order History',
    continueShopping: 'Continue Shopping',
    viewReceipt: 'View Receipt',
    guestPrompt: 'Create an account to track this order',
    guestPromptDesc: 'Sign up to view your order history, track deliveries, and reorder with one click.',
    guestPromptCta: 'Create Account',
  },
  orderDetails: {
    title: 'Order',
    orderedOn: 'Ordered on',
    orderTimeline: 'Order Timeline',
    orderStatus: 'Current Status',
    orderSummary: 'Order Summary',
    itemsOrdered: 'Items Ordered',
    shippingAddress: 'Shipping Address',
    shipmentInfo: 'Shipment Information',
    awbCode: 'AWB Code',
    courier: 'Courier',
    tracking: 'Tracking',
    viewTracking: 'Track Package',
    estDelivery: 'Estimated Delivery',
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    free: 'Free',
    tax: 'Tax',
    total: 'Total',
    printInvoice: 'Print Invoice',
    viewReceipt: 'View Receipt',
    reorder: 'Reorder',
    helpSupport: 'Help & Support',
    needHelp: 'Need help with your order?',
    contactSupport: 'Contact Support',
    backToOrders: 'Back to Orders',
    quantity: 'Quantity',
    price: 'Price',
    loadingOrder: 'Loading order details...',
    orderNotFound: 'Order not found.',
    failedToLoad: 'Failed to load order details.',
  },
  adminShipment: {
    // Table columns
    shipmentStatusCol: 'Shipment',
    awbCol: 'AWB / Courier',
    // Filters
    filterAll: 'All Shipments',
    filterProcessing: 'Processing',
    filterRetrying: 'Retrying',
    filterFailed: 'Failed',
    filterShipped: 'Shipped',
    filterNotStarted: 'Not Started',
    // Badge counter
    failedShipmentsAlert: 'Failed Shipments — click to filter',
    // AWB display
    awbPending: 'Pending',
    // Expand / row
    expandTitle: 'View shipment details',
    // Retry button (table row)
    retryButton: 'Retry Shipment',
    retryButtonTitle: 'Force-retry shipment creation via admin queue',
    retryInFlight: 'Queued…',
    retryQueuedToast: 'Retry queued — refresh in a few seconds to see updated status.',
    // Retry modal
    retryModalTitle: 'Retry Shipment',
    retryModalSubtitle: 'This will queue a new shipment creation attempt. The result is async — check back in a few seconds.',
    retryOrderId: 'Order ID',
    retryReason: 'Reason for retry',
    optional: 'optional',
    retryReasonPlaceholder: 'e.g. Address confirmed correct, courier issue resolved…',
    retryConfirm: 'Queue Retry',
    retryInProgress: 'Queuing…',
    retryAsyncNote: '⚡ Retry is async — shipment creation happens in the background. The order will update within 30 seconds.',
    // Retry modal response messages
    retryQueued: 'Retry queued successfully. The shipment will be created in the background — refresh in a few seconds.',
    retryAlreadyCreated: 'This shipment was already created successfully. Refresh the order to see the latest status.',
    retryRateLimit: 'You are being rate limited. Please wait a moment before trying again.',
    retryServiceUnavailable: 'Shipment service is currently unavailable. Please try again later.',
    retryNotReady: 'Order is not ready for retry yet. Please wait and try again.',
    retryError: 'Failed to queue retry. Please try again.',
    close: 'Close',
    cancel: 'Cancel',
    // Detail panel
    detailSectionTitle: 'Shipment Details',
    detailStatus: 'Status',
    detailShipmentId: 'Shipment ID',
    detailAwb: 'AWB Code',
    detailCourier: 'Courier',
    detailTracking: 'Tracking URL',
    trackLink: 'Track Package',
    detailShippingStatus: 'Shipping Status',
    detailEdd: 'Est. Delivery',
    detailAsyncNote: '⚡ Shipment is being created asynchronously — data will update once the background worker completes.',
    noShipmentData: 'No shipment data yet. Order may predate the async pipeline, or shipment creation is pending.',
  },
  adminMonitor: {
    // Page header
    title: 'Shipment Monitor',
    subtitle: 'Real-time operations dashboard — detect and fix shipment issues',
    navTitle: 'Shipment Monitor',
    navDesc: 'Real-time shipment operations — detect failures, stuck orders, SLA breaches',
    // Metric cards
    metricProcessing: 'Processing',
    metricRetrying: 'Retrying',
    metricFailed: 'Permanent Failed',
    metricCompleted: 'Shipped Today',
    metricStuck: 'Stuck (>10 min)',
    metricSlaBreach: 'SLA Breached',
    // Alerts section
    alertsTitle: 'Needs Attention',
    alertStuck: 'Stuck Order',
    alertSla: 'SLA Breached',
    alertFailed: 'Permanent Failure',
    noAlerts: 'No critical issues — all shipments healthy',
    // Filters
    filterAll: 'All',
    filterProcessing: 'Processing',
    filterRetrying: 'Retrying',
    filterFailed: 'Failed',
    filterShipped: 'Shipped',
    filterStuck: 'Stuck',
    filterSlaBreach: 'SLA Breached',
    filterNeedsAttention: 'Needs Attention',
    timeFilter1h: 'Last 1h',
    timeFilter24h: 'Last 24h',
    timeFilterAll: 'All time',
    // Table columns
    colOrderId: 'Order ID',
    colCustomer: 'Customer',
    colPayment: 'Payment',
    colShipment: 'Shipment',
    colAwb: 'AWB',
    colFailureReason: 'Failure Reason',
    colDuration: 'Duration',
    colSla: 'SLA Deadline',
    colActions: 'Actions',
    // Table content
    awbPending: 'Pending',
    noOrders: 'No orders match the current filters.',
    noShipmentData: 'No shipment data available.',
    detailTitle: 'Full Shipment Record',
    trackLink: 'Open tracking',
    // Auto-refresh
    autoRefresh: 'Auto-refresh',
    autoRefreshOn: 'ON',
    autoRefreshOff: 'OFF',
    lastUpdated: 'Last updated',
    manualRefresh: 'Refresh now',
    refreshing: 'Refreshing…',
    // Actions
    actionRetry: 'Retry',
    actionDetails: 'Details',
    // Retry modal
    retryModalTitle: 'Retry Shipment',
    retryModalSubtitle: 'Queue a new shipment creation attempt (async — check back in ~30s)',
    retryOrderId: 'Order ID',
    retryReason: 'Reason for retry',
    optional: 'optional',
    retryReasonPlaceholder: 'e.g. Address confirmed, courier issue resolved…',
    retryConfirm: 'Queue Retry',
    retryInProgress: 'Queuing…',
    cancel: 'Cancel',
    close: 'Close',
    retryQueued: 'Retry queued — refresh in a few seconds.',
    retryQueuedToast: 'Retry queued — refresh to see updated status.',
    retryAlreadyCreated: 'Shipment already created. Refresh to see latest status.',
    retryRateLimit: 'Rate limited — please wait before retrying.',
    retryServiceUnavailable: 'Shipment service is unavailable. Try again later.',
    retryError: 'Retry failed. Please try again.',
    retryNotReady: 'Order not ready for retry yet.',
    retryAsyncNote: '⚡ Retry is async — the order will update within 30 seconds.',
  },
  reconciliationMonitor: {
    // Page header
    title: 'Reconciliation Monitor',
    subtitle: 'Live data-integrity dashboard — detect payment, order, and shipment inconsistencies',
    navTitle: 'Reconciliation Monitor',
    navDesc: 'Live monitoring of data integrity — orphaned payments, stuck orders, failed reconciliations',
    // Metric cards
    metricOrphaned: 'Orphaned Payments',
    metricOrphanedTitle: 'Paid but no shipment started (>15 min)',
    metricFailedReconciling: 'Failed Reconciling',
    metricFailedReconcilingTitle: 'Shipment creation retrying',
    metricPermanentFailed: 'Permanent Failed',
    metricPermanentFailedTitle: 'All retry attempts exhausted',
    metricStuck: 'Stuck (>10 min)',
    metricStuckTitle: 'Processing longer than threshold',
    metricSlaBreach: 'SLA Breached',
    metricSlaBreachTitle: 'Deadline passed, not completed',
    metricReconciled: 'Reconciled Today',
    metricReconciledTitle: 'Successfully completed in last 24h',
    // Alerts
    alertsTitle: 'Critical Issues — Needs Immediate Attention',
    noAlerts: 'No critical data integrity issues — all systems consistent',
    alertsOverflow: '+{n} more — use filters below to see all',
    // Controls
    autoRefresh: 'Auto-refresh',
    autoRefreshOn: 'ON',
    autoRefreshOff: 'OFF',
    lastUpdated: 'Last updated',
    manualRefresh: 'Refresh now',
    refreshing: 'Refreshing…',
    runReconciliation: 'Run reconciliation',
    reconRunning: 'Running…',
    reconciliationTriggered: '✓ Reconciliation triggered — results will appear shortly',
    exportCsv: 'Export CSV',
    // Alert actions
    actionView: 'View',
    actionRetry: 'Retry',
    actionReconcile: 'Reconcile',
    // Filters
    filterLabel: 'Filter:',
    filterAll: 'All',
    filterOrphaned: 'Orphaned Payments',
    filterFailedReconciling: 'Retrying',
    filterPermanentFailed: 'Permanent Failed',
    filterStuck: 'Stuck',
    filterSlaBreach: 'SLA Breached',
    filterNeedsAttention: 'Needs Attention',
    timeFilter1h: 'Last 1h',
    timeFilter24h: 'Last 24h',
    timeFilterAll: 'All time',
    // Table columns
    colOrderId: 'Order ID',
    colPayment: 'Payment',
    colShipment: 'Shipment',
    colIssueType: 'Issue Type',
    colDuration: 'Processing',
    colSla: 'SLA Deadline',
    colLastUpdated: 'Last Updated',
    colActions: 'Actions',
    // Table content
    noOrders: 'No orders match the current filters.',
    noShipmentData: 'No shipment data available.',
    detailTitle: 'Full Record',
    // Bulk actions
    selectedOrders: 'orders selected',
    bulkRetry: 'Retry all selected',
    bulkRetrying: 'Queuing…',
    clearSelection: 'Clear',
    // Tabs
    tabHealth: '🟢 Live Health',
    tabMismatches: '🔎 Mismatches',
  },
  receipt: {
    title: 'Receipt',
    receiptNumber: 'Receipt No.',
    issuedDate: 'Issued',
    orderId: 'Order ID',
    customerInfo: 'Customer',
    billingAddress: 'Billing Address',
    orderItems: 'Order Items',
    columnProduct: 'Product',
    columnQty: 'Qty',
    columnUnit: 'Unit Price',
    columnTotal: 'Total',
    subtotal: 'Subtotal',
    tax: 'Tax',
    shipping: 'Shipping',
    total: 'Total',
    free: 'Free',
    paymentInfo: 'Payment',
    paymentMethod: 'Method',
    paymentId: 'Payment ID',
    paymentStatus: 'Status',
    paid: 'Paid',
    printReceipt: 'Print Receipt',
    downloadPdf: 'Download PDF',
    backToOrder: 'Back to Order',
    currency: '₹',
    loading: 'Loading receipt…',
    errorTitle: 'Could not load receipt',
    errorNotFound: 'This receipt is not available. The order may not have been paid yet.',
    errorForbidden: 'You do not have permission to view this receipt.',
    errorRateLimit: 'Too many requests — please wait a moment and try again.',
    errorGeneric: 'Something went wrong. Please try again.',
    pdfError: 'PDF download failed. Please try again.',
    pdfRateLimit: 'Too many requests — please wait a moment before downloading.',
    tryAgain: 'Try Again',
  },
};

export default en;
